import React, { useState } from 'react';
import AppPagePanel from 'components/ui/AppPagePanel';
import { useFeatureAccess } from '../../pricing';

interface Props {
    /** The live (possibly unsaved) draft text. Controlled by the parent so the generate
     *  actions in InsightsSummaryPanel can read the latest text without waiting for the save. */
    value: string;
    /** The value currently persisted on the server — used only to skip no-op saves on blur. */
    persisted: string;
    /** Called on every keystroke so the parent's live draft stays in sync. */
    onChange: (text: string) => void;
    /**
     * Persists a new value for the custom instructions. Resolves with an error message if the
     * server's guardrail rejected the text (e.g. it deviates from tailoring the summary/opportunities),
     * or null on success.
     */
    onCommit: (text: string) => Promise<string | null>;
}

/**
 * Standalone panel (under InsightsSummaryPanel) holding the optional custom-instructions
 * textarea. A single shared value feeds both InsightsSummaryPanel's "Update Summary" and
 * "↻ Regenerate All" actions — whatever is saved here is used the next time either runs.
 *
 * Gated behind the same tier as `insights_ai_summary` (Pro/Teams) since this panel only
 * exists to tailor that feature. The server independently enforces the same gate on
 * PATCH /custom-ai-instructions and strips a forged `customInstructions` value on the
 * generation endpoints, so this client-side lock is a UX layer, not the only defense.
 */
export default function CustomInstructionsPanel({ value, persisted, onChange, onCommit }: Props) {
    const { access, tryAccess } = useFeatureAccess('insights_ai_summary');
    const isLocked = access === 'locked';

    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const handleBlur = async () => {
        setError(null);
        if (isLocked) return;
        if (value.trim() === persisted.trim()) return;

        setIsSaving(true);
        const err = await onCommit(value);
        setIsSaving(false);

        if (err) setError(err);
    };

    // Locked users cannot focus the field long enough to type anything: show the
    // upgrade prompt and immediately hand focus back, rather than letting them linger
    // in a field that (per the server-side gate) could never actually save.
    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        if (!isLocked) return;
        tryAccess();
        e.target.blur();
    };

    return (
        <AppPagePanel
            title='Custom AI Instructions'
            style={{ minWidth: 0, flex: 1, minHeight: 0 }}
            className='custom-instructions-panel'
            actions={isLocked ? <span className='tier-badge'>Pro / Teams</span> : undefined}
        >
            <p className='custom-instructions-panel__hint'>
                Optional — tailor how the summary and opportunity suggestions below are generated
                (e.g. tone, focus areas, which themes to prioritize). Applied next time you click
                Update Summary or Regenerate All.
            </p>
            <textarea
                className='custom-instructions-panel__input'
                value={isLocked ? '' : value}
                onChange={(e) => !isLocked && onChange(e.target.value)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                placeholder={
                    isLocked
                        ? 'Available on Pro and Teams plans.'
                        : 'e.g. Focus the summary on onboarding friction, or group opportunities by user segment.'
                }
                maxLength={500}
                readOnly={isLocked}
                disabled={isSaving}
                title={isLocked ? 'Available on Pro and Teams plans' : undefined}
            />
            <div className='custom-instructions-panel__footer'>
                <span className='custom-instructions-panel__count'>{isLocked ? '0/500' : `${value.length}/500`}</span>
                {isSaving && <span className='custom-instructions-panel__status'>Saving…</span>}
            </div>
            {error && <p className='custom-instructions-panel__error'>{error}</p>}
        </AppPagePanel>
    );
}
